import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import * as React from "react";
import { cn } from "../lib/cn";

// ui-spec §8.1, dashboard theme: default = the one accent-outlined primary per page ("start something new"),
// outline = secondary, ghost = row actions and icon buttons; 13px `ui` text at the density's control height.
export const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-ui font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "border border-primary-border bg-primary text-primary-foreground hover:bg-primary-hover",
        outline: "border border-secondary-border bg-transparent text-secondary-foreground hover:bg-secondary-hover",
        ghost: "text-fg-secondary hover:bg-ghost-hover hover:text-fg-primary",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive-hover",
        link: "h-auto px-0 text-fg-secondary underline-offset-4 hover:text-fg-primary hover:underline",
      },
      size: {
        default: "h-[var(--nd-control-height)] px-3",
        sm: "h-[var(--nd-control-height-sm)] px-2.5",
        icon: "size-[var(--nd-control-height-sm)]",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild = false, type, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="button"
      type={asChild ? undefined : (type ?? "button")}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
